import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, NewReleases as ReleaseIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Route as RouteIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface ReleaseVersion {
  version: string;
  file: string;
  date: string;
  title: string;
  current?: boolean;
}

interface ReleaseManifest {
  versions: ReleaseVersion[];
}

interface PendingRelease {
  file: string;
  title: string;
}

interface PendingManifest {
  pending: PendingRelease[];
}

interface ReleaseNotesModalProps {
  open: boolean;
  onClose: () => void;
}

export const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<ReleaseManifest | null>(null);
  const [pendingManifest, setPendingManifest] = useState<PendingManifest | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<ReleaseVersion | null>(null);
  const [selectedPending, setSelectedPending] = useState<PendingRelease | null>(null);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingExpanded, setPendingExpanded] = useState(false);

  // Fetch manifest on open
  useEffect(() => {
    if (open && !manifest) {
      fetch('/release-notes/manifest.json')
        .then((res) => res.json())
        .then((data: ReleaseManifest) => {
          setManifest(data);
          // Auto-select current version
          const current = data.versions.find((v) => v.current) || data.versions[0];
          if (current) {
            setSelectedVersion(current);
          }
        })
        .catch(() => setError('Failed to load release notes'));

      // Also fetch pending releases manifest
      fetch('/release-notes/pending-release/pending-manifest.json')
        .then((res) => (res.ok ? res.json() : { pending: [] }))
        .then((data: PendingManifest) => setPendingManifest(data))
        .catch(() => setPendingManifest({ pending: [] }));
    }
  }, [open, manifest]);

  // Fetch content when version or pending selection changes
  useEffect(() => {
    const filePath = selectedPending
      ? `/release-notes/pending-release/${selectedPending.file}`
      : selectedVersion
        ? `/release-notes/${selectedVersion.file}`
        : null;
    if (!filePath) return;

    setLoading(true);
    setError(null);
    fetch(filePath)
      .then((res) => res.text())
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load release notes content');
        setLoading(false);
      });
  }, [selectedVersion, selectedPending]);

  // Simple markdown to HTML conversion
  const renderMarkdown = (md: string) => {
    // Split into lines for better processing
    const lines = md.split('\n');
    let html = '';
    let inList = false;
    let inCodeBlock = false;
    let codeBlockContent = '';
    let inTable = false;
    let tableRows: string[] = [];

    const processTableRows = (rows: string[]) => {
      if (rows.length < 2) return '';
      let tableHtml = '<table>';
      rows.forEach((row, idx) => {
        // Skip separator row (|---|---|)
        if (row.match(/^\|[\s-:|]+\|$/)) return;
        const cells = row.split('|').filter(c => c.trim() !== '');
        const tag = idx === 0 ? 'th' : 'td';
        tableHtml += '<tr>';
        cells.forEach(cell => {
          tableHtml += `<${tag}>${cell.trim()}</${tag}>`;
        });
        tableHtml += '</tr>';
      });
      tableHtml += '</table>';
      return tableHtml;
    };

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      // Handle code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          html += `<pre><code>${codeBlockContent}</code></pre>`;
          codeBlockContent = '';
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        continue;
      }

      if (inCodeBlock) {
        codeBlockContent += line + '\n';
        continue;
      }

      // Handle tables (lines starting and ending with |)
      const isTableRow = line.trim().startsWith('|') && line.trim().endsWith('|');
      if (isTableRow) {
        if (!inTable) {
          if (inList) { html += '</ul>'; inList = false; }
          inTable = true;
          tableRows = [];
        }
        tableRows.push(line);
        continue;
      } else if (inTable) {
        html += processTableRows(tableRows);
        inTable = false;
        tableRows = [];
      }

      // Skip empty lines but close lists
      if (line.trim() === '') {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        continue;
      }

      // Horizontal rules
      if (line.trim() === '---') {
        if (inList) { html += '</ul>'; inList = false; }
        html += '<hr />';
        continue;
      }

      // Headers
      if (line.startsWith('### ')) {
        if (inList) { html += '</ul>'; inList = false; }
        html += `<h3>${line.slice(4)}</h3>`;
        continue;
      }
      if (line.startsWith('## ')) {
        if (inList) { html += '</ul>'; inList = false; }
        html += `<h2>${line.slice(3)}</h2>`;
        continue;
      }
      if (line.startsWith('# ')) {
        if (inList) { html += '</ul>'; inList = false; }
        html += `<h1>${line.slice(2)}</h1>`;
        continue;
      }

      // List items
      const listMatch = line.match(/^\s*[-*]\s+(.*)$/) || line.match(/^\s*\d+\.\s+(.*)$/);
      if (listMatch) {
        if (!inList) {
          html += '<ul>';
          inList = true;
        }
        let content = listMatch[1];
        // Process inline formatting
        content = content
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/`([^`]+)`/g, '<code>$1</code>')
          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
        html += `<li>${content}</li>`;
        continue;
      }

      // Regular paragraph
      if (inList) { html += '</ul>'; inList = false; }
      let content = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
      html += `<p>${content}</p>`;
    }

    if (inList) html += '</ul>';
    if (inTable) html += processTableRows(tableRows);
    return html;
  };

  const formatVersion = (version: string) => `V${version}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReleaseIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Release Notes
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Order Lifecycle interactive guide banner */}
        <Box
          onClick={() => {
            onClose();
            navigate('/help/order-lifecycle');
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.25,
            cursor: 'pointer',
            flexShrink: 0,
            background: 'linear-gradient(150deg, #0a5740, #073c2c 82%)',
            color: '#eafaf1',
            '&:hover': { opacity: 0.92 },
          }}
        >
          <RouteIcon sx={{ fontSize: 22, color: '#4dd693' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" fontWeight={700}>
              The Life of an Order
            </Typography>
            <Typography variant="caption" sx={{ color: '#bfe6cf' }}>
              Interactive guide to the full order journey — stages, Patient 360 &amp; scenarios
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ fontSize: 18, color: '#4dd693' }} />
        </Box>

        <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        {/* Version List Sidebar */}
        <Box
          sx={{
            width: 220,
            minWidth: 220,
            borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            overflowY: 'auto',
          }}
        >
          {/* Pending Releases Section (collapsed by default) */}
          {pendingManifest && pendingManifest.pending.length > 0 && (
            <>
              <Box
                onClick={() => setPendingExpanded(!pendingExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.04) },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Pending Releases
                  </Typography>
                  <Chip
                    label={pendingManifest.pending.length}
                    size="small"
                    color="warning"
                    sx={{ height: 18, fontSize: '0.65rem', minWidth: 24 }}
                  />
                </Box>
                {pendingExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                )}
              </Box>
              {pendingExpanded && (
                <List dense disablePadding>
                  {pendingManifest.pending.map((item) => (
                    <ListItemButton
                      key={item.file}
                      selected={selectedPending?.file === item.file}
                      onClick={() => {
                        setSelectedPending(item);
                        setSelectedVersion(null);
                      }}
                      sx={{
                        borderLeft: selectedPending?.file === item.file
                          ? `3px solid ${theme.palette.warning.main}`
                          : '3px solid transparent',
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.warning.main, 0.08),
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography fontWeight={600} fontSize="0.85rem">
                            {item.title}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Published Versions Section */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 2,
              py: 1.5,
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Versions
          </Typography>
          <List dense disablePadding>
            {manifest?.versions.map((version) => (
              <ListItemButton
                key={version.version}
                selected={selectedVersion?.version === version.version && !selectedPending}
                onClick={() => {
                  setSelectedVersion(version);
                  setSelectedPending(null);
                }}
                sx={{
                  borderLeft: selectedVersion?.version === version.version && !selectedPending
                    ? `3px solid ${theme.palette.primary.main}`
                    : '3px solid transparent',
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600} fontSize="0.9rem">
                        {formatVersion(version.version)}
                      </Typography>
                      {version.current && (
                        <Chip
                          label="Current"
                          size="small"
                          color="primary"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {version.date} - {version.title}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Box
              sx={{
                '& h1': {
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mt: 0,
                  mb: 2,
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  pb: 1,
                },
                '& h2': {
                  fontSize: '1.35rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mt: 3,
                  mb: 1.5,
                },
                '& h3': {
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mt: 2,
                  mb: 1,
                },
                '& p': {
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                  color: theme.palette.text.secondary,
                  my: 1,
                },
                '& li': {
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                },
                '& ul': {
                  pl: 3,
                  my: 1,
                },
                '& code': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.85em',
                },
                '& pre': {
                  bgcolor: theme.palette.grey[900],
                  color: theme.palette.grey[100],
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': {
                    bgcolor: 'transparent',
                    p: 0,
                  },
                },
                '& hr': {
                  border: 'none',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  my: 3,
                },
                '& a': {
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
                '& strong': {
                  fontWeight: 600,
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  my: 2,
                  fontSize: '0.875rem',
                },
                '& th, & td': {
                  border: `1px solid ${theme.palette.divider}`,
                  px: 2,
                  py: 1,
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                },
                '& td': {
                  color: theme.palette.text.secondary,
                },
                '& tr:nth-of-type(even) td': {
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                },
              }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
            />
          )}
        </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseNotesModal;
